import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DirectionalHint, FontIcon, Stack, Text, TextField, TooltipHost } from '@fluentui/react';
import { ApiCalls } from '../../../../../../../../api/api';
import { useToggle } from '../../../../../../../../hooks/useToggle';
import { useRowMetaContext } from '../../../../context/RowMetaContext';
import { TimeEntryApprovalDtoState, useApprovalState, useActivityState } from '../../../../../../../../_grid/grid3';

function useApprovedHoursSync() {
    const { row } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const [isEditing, toggleEditing] = useToggle(false);
    const [approvalState, setApproval] = useApprovalState(row.id);
    const [activity] = useActivityState(row.id);
    const { originalHours, originalApproved } = useMemo(
        () => ({
            originalHours: row.timeEntry.hours.toNumber(),
            originalApproved: row.approvedHours,
        }),
        [row.timeEntry.hours, row.approvedHours],
    );

    // Local copy of approved hours (null means “not set”)
    const [localApproved, setLocalApproved] = useState<number | null>(originalApproved ?? null);
    const hasCustomApproval = localApproved !== null;

    const { mutate: saveApproved } = useMutation((value: number | null) =>
        ApiCalls.setApprovedHoursForApproval({
            timeEntryId: row.timeEntry.id,
            approvedHours: value,
        }),
    );

    const resetApprovalIfEditable = useCallback(() => {
        if (approvalState === 'Approved' && activity?.allowEditAfterApproval) {
            setApproval('Pending');
        }
    }, [approvalState, activity?.allowEditAfterApproval, setApproval]);

    const handleBlur = useCallback(() => {
        resetApprovalIfEditable();
        if (localApproved !== null && !Number.isNaN(localApproved)) {
            saveApproved(localApproved);
        } else {
            saveApproved(null);
        }
        toggleEditing();
    }, [localApproved, resetApprovalIfEditable, saveApproved, toggleEditing]);

    // Keep the localApproved in sync with user typing
    const handleChange = useCallback((_: React.FormEvent<HTMLInputElement>, newValue?: string) => {
        const parsed = newValue ? parseFloat(newValue) : NaN;
        setLocalApproved(Number.isNaN(parsed) ? 0 : parsed);
    }, []);

    // Reset back to default
    const handleReset = useCallback(() => {
        resetApprovalIfEditable();
        setLocalApproved(null);
        saveApproved(null);
    }, [resetApprovalIfEditable, saveApproved]);

    return {
        isEditing,
        toggleEditing,
        originalHours,
        localApproved,
        hasCustomApproval,
        approvalState,
        activity,
        handleBlur,
        handleChange,
        handleReset,
    };
}

export const HoursApprovedColumn: React.FC = () => {
    const { isEditing, toggleEditing, originalHours, localApproved, hasCustomApproval, approvalState, activity, handleBlur, handleChange, handleReset } =
        useApprovedHoursSync();
    const displayValue = hasCustomApproval ? localApproved : `Default (${originalHours})`;
    const isLocked = approvalState === 'Approved' && !activity?.allowEditAfterApproval;

    return (
        <Stack horizontal verticalAlign="center">
            {isEditing ? (
                <TextField
                    type="number"
                    value={(localApproved ?? originalHours).toString()}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus
                    min={0}
                    max={24}
                    step={0.5}
                />
            ) : (
                <>
                    <Stack.Item grow>
                        <Text className="subGroupName textOverflow">{displayValue}</Text>
                    </Stack.Item>

                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} style={{ marginRight: 80 }}>
                        {!isLocked ? (
                            <>
                                <TooltipHost content="Set billable adjustment" directionalHint={DirectionalHint.topLeftEdge} closeDelay={250}>
                                    <FontIcon iconName="Edit" data-testid="edit" style={{ fontSize: 12, cursor: 'pointer' }} onClick={() => toggleEditing()} />
                                </TooltipHost>
                                <TooltipHost content="Revert to default" directionalHint={DirectionalHint.topLeftEdge} closeDelay={250}>
                                    <FontIcon iconName="RevToggleKey" data-testid="delete" style={{ fontSize: 12, cursor: 'pointer' }} onClick={handleReset} />
                                </TooltipHost>
                            </>
                        ) : (
                            <TooltipHost content="Cannot edit approved row" directionalHint={DirectionalHint.topLeftEdge} closeDelay={250}>
                                <FontIcon iconName="Blocked" data-testid="blocked" style={{ fontSize: 12, cursor: 'pointer' }} />
                            </TooltipHost>
                        )}
                    </Stack>
                </>
            )}
        </Stack>
    );
};
